/**
 * The row of chips a finished session carries: how the client felt, the session's dominant signal,
 * and a counter for whatever the three-chip cap left out.
 *
 * **At most three, always one line.** Which three, and why, is `model/sessionVerdict` — this file
 * draws what it is handed and decides nothing.
 *
 * The pill is the house chip (`shared/ui/neutralChip.module.css`) and **colour goes on the GLYPH
 * and on nothing else** — the owner's decision of 16 September 2026, which reversed the numeral
 * arrangement recorded in `docs/design/README.md` and is recorded there in its place.
 *
 * The row is drawn even when it is EMPTY. The chips come from the session's second read, so the row
 * reserves its height in CSS whether or not they are there, holds one gated skeleton chip while the
 * WAIT runs, and keeps the same height for a session that turned out to have nothing to say.
 *
 * The wait is the gate's, never the read's: `gate.waiting` stays true until the hole has served its
 * minimum life, and the chips arrive on `gate.reveal` in the same frame it retires. Forking on
 * `pending` swapped the hole for the chips the moment the answer landed and then blinked the chips
 * to transparent 300ms later, which is the pair of jumps `shared/lib/useSkeletonGate` describes.
 */

import skel from '~/shared/ui/skeleton.module.css';
import chip from '~/shared/ui/neutralChip.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';

import type { SessionSignal, SignalInk } from '../model/sessionVerdict';
import { SignalGlyphMark, SleepMark } from './HomeGlyphs';
import styles from './signalChips.module.css';

/** `model/` names the ink; the stylesheet decides what each name is worth in each theme. */
const INK: Readonly<Record<SignalInk, string | undefined>> = {
  neutral: styles.inkNeutral,
  warn: styles.inkWarn,
  danger: styles.inkDanger,
  good: styles.inkGood,
  record: styles.inkRecord,
};

interface FeedbackSignalChipsProps {
  readonly signals: readonly SessionSignal[];
  /** The session's second read has not landed. See the header for why the row is drawn regardless. */
  readonly pending: boolean;
  /**
   * The night before, worded ("Sen 6,1 h") — the task card's fourth chip (KANON "Karty zadań",
   * 24 September 2026). `null` where the client did not say, and no chip is drawn for it
   * (`completed-session-display` R11). The Pulpit's row passes none: its line is three chips.
   */
  readonly sleep?: string | null;
}

export function FeedbackSignalChips({ signals, pending, sleep = null }: FeedbackSignalChipsProps) {
  const gate = useSkeletonGate(pending);

  if (gate.waiting) {
    return (
      <div className={styles.chipRow} aria-hidden="true">
        <span className={`${styles.chipHole} ${gate.skeleton ? skel.bar : ''}`} />
      </div>
    );
  }

  return (
    <div className={`${styles.chipRow} ${gate.reveal}`}>
      {signals.map((signal) => (
        <span
          className={`${chip.neutralChip} ${styles.chip} ${INK[signal.ink] ?? ''}`}
          key={signal.id}
        >
          {/* The counter carries no glyph: it is not a thing the client did. */}
          {signal.id === 'more' ? null : (
            <SignalGlyphMark className={styles.chipMark} id={signal.id} />
          )}
          {/* All three runs inside ONE element. The chip is a flex container, so a bare text node
            * beside the mark becomes an anonymous flex ITEM — the gap then falls between every run
            * and the leading and trailing spaces inside each are stripped, which drew
            * "Nastrój  7  / 10" with the sentence's own spacing replaced by the chip's. */}
          <span className={styles.chipText}>
            {signal.before}
            <b className={styles.chipValue}>{signal.value}</b>
            {signal.after}
          </span>
          {/* The counter's own accessible tail — the signals it stands for, named in full, read
            * straight after the words they belong to. `model/` spells it, including its leading
            * punctuation; `''` on every real chip, so nothing else grows an empty element. */}
          {signal.detail === '' ? null : (
            <span className={hidden.srOnly}>{signal.detail}</span>
          )}
        </span>
      ))}
      {sleep === null ? null : (
        <span className={`${String(chip.neutralChip)} ${String(styles.chip)} ${String(styles.inkNeutral)}`}>
          <SleepMark className={styles.chipMark} />
          <span className={styles.chipText}>{sleep}</span>
        </span>
      )}
    </div>
  );
}
